import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Users, Bitcoin, Flame, ArrowRight, Sparkles, Gift, TrendingUp,
} from 'lucide-react';
import api from '../../../services/api';

/**
 * PromoBanners (Iter 145++)
 * =========================
 * Tenant-scoped auto-sliding promo carousel.
 *
 *   • Fetches banners from `GET /api/promo-banners` (X-Tenant-Slug aware).
 *   • Starts empty while loading so tenant refreshes never flash old platform
 *     banners before the tenant-scoped API response arrives.
 *   • Single-card auto-slide (6 s loop, infinite). Manual swipe + dot-click
 *     supported. Pauses for 5.5 s on touch.
 *   • Each auto-advance plays a subtle "throb" (480 ms scale-bounce) on the
 *     incoming banner so the player notices the change without it being
 *     jarring. The throb keyframe is restarted by toggling React keys.
 *
 * Admin: `/admin?tab=promo-banners`. Tenant admins write into their own
 * tenant_id, master admin writes platform-default banners.
 */

const ACTION_ICONS = { referral: Users, deposit: Bitcoin, url: Flame };
const BADGE_ICONS  = { referral: Gift,  deposit: TrendingUp, url: Sparkles };

const isPhoneVerifyBanner = (banner = {}) => {
  const text = [
    banner.title,
    banner.body,
    banner.eyebrow,
    banner.highlight,
    banner.cta,
  ].filter(Boolean).join(' ').toLowerCase();
  return (
    /verify\s+(your\s+)?(phone|number)/i.test(text) ||
    /(phone|number)\s+verif/i.test(text) ||
    /get\s*\$?\s*5.*freeplay/i.test(text)
  );
};

export default function PromoBanners({ hidePhoneVerify = false, variant = 'compact' }) {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  // Iter 145++: bumps every time activeIdx changes so the throb keyframe
  // restarts on the active banner. We add it to the React key of the
  // active card so React unmounts/mounts and replays the animation.
  const [throbKey, setThrobKey] = useState(0);
  const touch = useRef({ x: 0, y: 0, t: 0 });

  const basePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const isHero = variant === 'hero';
  const bannerMinHeight = isHero ? '190px' : '120px';
  const bannerRadius = isHero ? '24px' : '26px';

  // Fetch tenant-scoped banners. axios attaches X-Tenant-Slug automatically.
  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get('/api/promo-banners', {
      params: { placement: 'home', _refresh: Date.now() },
      headers: { 'Cache-Control': 'no-cache' },
    })
      .then((r) => {
        if (!live) return;
        const list = (r.data?.banners || []).filter((b) => (
          b.enabled !== false && (!b.image_only || !!b.image_url)
        )).filter((b) => !(hidePhoneVerify && isPhoneVerifyBanner(b)));
        setBanners(list);
        setActiveIdx(0);
      })
      .catch(() => {
        if (live) setBanners([]);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => { live = false; };
  }, [tenantSlug, hidePhoneVerify]);

  const handleClick = (banner) => {
    if (banner.redirect_enabled === false) return;
    if (banner.action === 'referral') {
      navigate(`${basePath}/referral`);
    } else if (banner.action === 'deposit') {
      window.dispatchEvent(new CustomEvent('vl:open-deposit'));
    } else if (banner.action === 'url' && banner.url) {
      if (banner.url.startsWith('http')) {
        window.open(banner.url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(banner.url.startsWith('/') ? banner.url : `${basePath}/${banner.url}`);
      }
    }
  };

  // Auto-advance every 6s — pauses while the user is interacting.
  useEffect(() => {
    if (paused) return undefined;
    if (banners.length < 2) return undefined;
    const t = setInterval(() => {
      setActiveIdx((i) => (i + 1) % banners.length);
      setThrobKey((k) => k + 1);
    }, 6000);
    return () => clearInterval(t);
  }, [paused, banners.length]);

  const pauseFor = (ms = 4500) => {
    setPaused(true);
    if (pauseFor.t) clearTimeout(pauseFor.t);
    pauseFor.t = setTimeout(() => setPaused(false), ms);
  };

  const onTouchStart = (e) => {
    const t0 = e.touches?.[0];
    if (!t0) return;
    touch.current = { x: t0.clientX, y: t0.clientY, t: Date.now() };
    pauseFor(5500);
  };
  const onTouchEnd = (e) => {
    const t1 = e.changedTouches?.[0];
    if (!t1) return;
    const dx = t1.clientX - touch.current.x;
    const dy = t1.clientY - touch.current.y;
    const dt = Date.now() - touch.current.t;
    if (dt > 600 || Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) setActiveIdx((i) => (i + 1) % banners.length);
    else setActiveIdx((i) => (i - 1 + banners.length) % banners.length);
    setThrobKey((k) => k + 1);
  };

  const resolveImage = (u) => {
    if (!u) return '';
    if (u.startsWith('http')) return u;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${u}`;
  };

  if (loading) {
    return <div data-testid="promo-banners-loading" style={{ minHeight: bannerMinHeight }} aria-hidden="true" />;
  }

  if (banners.length === 0) return null;

  return (
    <div data-testid="promo-banners" className={isHero ? 'space-y-2' : 'space-y-2'}>
      <div
        className="relative overflow-hidden"
        style={{
          borderRadius: bannerRadius,
          boxShadow: isHero ? '0 22px 42px rgba(0,0,0,0.46)' : undefined,
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${activeIdx * 100}%)` }}
        >
          {banners.map((b, i) => {
            const Icon = ACTION_ICONS[b.action] || Sparkles;
            const BadgeIcon = BADGE_ICONS[b.action] || Sparkles;
            const accent = b.accent || '#A78BFA';
            const accentDeep = b.accent_deep || '#004D61';
            const glowColor = `${accent}88`;
            const isActive = i === activeIdx;
            const imageOnly = !!b.image_only;
            const canRedirect = b.redirect_enabled !== false;
            const CardTag = canRedirect ? 'button' : 'div';
            // Restart throb keyframe by changing React key when active banner refreshes.
            const reactKey = `${b.id || i}-${isActive ? throbKey : 'idle'}`;
            return (
              <CardTag
                key={reactKey}
                {...(canRedirect ? { type: 'button', onClick: () => handleClick(b) } : {})}
                data-testid={`promo-banner-${b.id || i}`}
                data-active={isActive ? 'true' : 'false'}
                className={`shrink-0 w-full text-left overflow-hidden relative transition ${
                  canRedirect ? 'active:scale-[0.99] hover:brightness-110 cursor-pointer' : 'cursor-default'
                }`}
                style={{
                  borderRadius: bannerRadius,
                  background: b.image_url
                    ? (imageOnly ? `url("${resolveImage(b.image_url)}") center/cover no-repeat` : `${b.bg_gradient}, url("${resolveImage(b.image_url)}") center/cover`)
                    : b.bg_gradient,
                  backgroundBlendMode: b.image_url && !imageOnly ? 'multiply' : 'normal',
                  border: `1.5px solid ${accent}66`,
                  boxShadow: imageOnly
                    ? '0 12px 26px rgba(0,0,0,0.38)'
                    : `0 16px 36px rgba(0,0,0,0.5), 0 0 0 1px ${accent}33, 0 0 30px ${glowColor}`,
                  animation: isActive ? 'promo-banner-throb 480ms cubic-bezier(0.34, 1.56, 0.64, 1) both' : undefined,
                }}
              >
                {/* Floating sparkle dots */}
                {!imageOnly && <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
                  {[
                    { l: '12%', t: '20%', d: '0s' },
                    { l: '78%', t: '16%', d: '0.6s' },
                    { l: '65%', t: '78%', d: '1.1s' },
                    { l: '20%', t: '74%', d: '0.3s' },
                  ].map((p, k) => (
                    <span
                      key={k}
                      className="absolute rounded-full"
                      style={{
                        left: p.l, top: p.t, width: 4, height: 4,
                        background: '#FFFFFF',
                        boxShadow: `0 0 10px ${accent}`,
                        animation: `promo-banner-sparkle 2.4s ease-in-out infinite ${p.d}`,
                        opacity: 0.55,
                      }}
                    />
                  ))}
                </div>}

                {/* Diagonal shimmer sweep */}
                {!imageOnly && <span
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background: 'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.18) 50%, transparent 65%)',
                    animation: 'promo-banner-shimmer 4s ease-in-out infinite',
                  }}
                  aria-hidden
                />}

                {imageOnly ? (
                  <div className="relative" style={{ minHeight: bannerMinHeight }} aria-label={b.title || 'Promo image'} />
                ) : (
                  <div
                    className="relative flex items-center gap-4 px-5 py-4 sm:px-6 sm:py-5"
                    style={{ minHeight: bannerMinHeight }}
                  >
                    <div className="relative shrink-0">
                      <div
                        className="absolute inset-0 rounded-2xl blur-xl"
                        style={{ background: accent, opacity: 0.55 }}
                        aria-hidden
                      />
                      <div
                        className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${accent} 0%, ${accentDeep} 100%)`,
                          boxShadow: `0 10px 22px ${glowColor}, inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 4px rgba(0,0,0,0.25)`,
                        }}
                      >
                        <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" strokeWidth={2.4} />
                        <span
                          className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{
                            background: '#0B0F0C',
                            border: `2px solid ${accent}`,
                            boxShadow: `0 0 10px ${glowColor}`,
                          }}
                        >
                          <BadgeIcon className="w-3 h-3" style={{ color: accent }} strokeWidth={2.6} />
                        </span>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1"
                        style={{ color: accent, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                      >
                        {b.eyebrow || 'Promo'}
                      </p>
                      <p
                        className="display font-extrabold leading-tight text-white"
                        style={{
                          fontSize: 'clamp(15px, 4.2vw, 19px)',
                          textShadow: '0 1px 2px rgba(0,0,0,0.55)',
                        }}
                      >
                        {b.title}
                        {b.highlight ? (
                          <>
                            {' · '}
                            <span style={{ color: accent, textShadow: `0 0 14px ${glowColor}` }}>
                              {b.highlight}
                            </span>
                          </>
                        ) : null}
                      </p>
                      <p
                        className="text-xs sm:text-sm mt-1 leading-snug line-clamp-2"
                        style={{ color: 'rgba(255,255,255,0.85)', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                      >
                        {b.body}
                      </p>
                    </div>

                    <div
                      className="shrink-0 px-3 py-2 rounded-full flex items-center gap-1.5 text-[11px] font-bold transition group-hover:translate-x-0.5"
                      style={{
                        background: 'rgba(0,0,0,0.45)',
                        border: `1px solid ${accent}66`,
                        color: '#fff',
                        backdropFilter: 'blur(6px)',
                      }}
                    >
                      <span className="hidden sm:inline">{b.cta}</span>
                      <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.6} />
                    </div>
                  </div>
                )}
              </CardTag>
            );
          })}
        </div>
      </div>

      {/* Pagination dots */}
      <div className="flex items-center justify-center gap-1.5 pt-1">
        {banners.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { pauseFor(6000); setActiveIdx(i); setThrobKey((k) => k + 1); }}
            className="rounded-full transition-all"
            style={{
              width: i === activeIdx ? '22px' : '6px',
              height: '6px',
              background: i === activeIdx ? '#FFFFFF' : 'rgba(255,255,255,0.35)',
            }}
            aria-label={`Go to banner ${i + 1}`}
            data-testid={`promo-banner-dot-${i}`}
          />
        ))}
      </div>

      <style>{`
        @keyframes promo-banner-shimmer {
          0%, 100% { transform: translateX(-110%); }
          50%      { transform: translateX(110%); }
        }
        @keyframes promo-banner-sparkle {
          0%, 100% { opacity: 0.4; transform: scale(0.8); }
          50%      { opacity: 0.9; transform: scale(1.4); }
        }
        /* Iter 145++: subtle throb on auto-advance. Spring-overshoot scale
           + a small brightness uplift so the eye is drawn to the new
           banner without it being jarring. */
        @keyframes promo-banner-throb {
          0%   { transform: scale(0.96); filter: brightness(1); }
          40%  { transform: scale(1.03); filter: brightness(1.12); }
          70%  { transform: scale(0.995); filter: brightness(1.04); }
          100% { transform: scale(1); filter: brightness(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="promo-banners"] *,
          [data-testid="promo-banners"]::before { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
